import styled, { css } from "styled-components";
import type { StatusVariant } from "./utils/rentProcessStatus";

export const Container = styled.div`
    min-height: 100vh;
    background: #f6f7fb;
    display: flex;   
    flex-direction: column; 
`;

export const Content = styled.div`
    width: 100%;
    max-width: 1380px;
    margin: 0 auto;
    padding: 18px clamp(16px, 3vw, 28px) 28px;
    flex: 1;
`;

export const HeaderLayer = styled.div`
  position: relative;
  z-index: 1;
`;

export const FooterLayer = styled.div`
  position: relative;
  z-index: 0;
`;

export const PageTitle = styled.div`
  margin: 8px 0 14px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
  }

  p {
    margin: 6px 0 0;
    font-size: 13px;
    color: #6b7280;
  }

  @media (max-width: 640px) {
    h2 {
      font-size: 20px;
    }
    p {
      font-size: 14px;
      line-height: 1.35;
    }
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 14px;

  display: flex;
  gap: 12px;
  align-items: center;

  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 26px rgba(16, 24, 40, 0.1);
    border-color: #e4e7ee;
  }

  /* MOBILE: 1 coluna (imagem em cima, info embaixo e botão no final) */
  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
      "photo"
      "info"
      "actions";
    gap: 12px;
    align-items: stretch;
  }
`;

export const Photo = styled.img`
  width: 92px;
  height: 92px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f3f4f6;

  @media (max-width: 640px) {
    grid-area: photo;
    width: 100%;
    height: 170px; /* ajuste fino: 160~200 */
    border-radius: 14px;
  }
`;

export const Info = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 640px) {
    grid-area: info;
  }
`;

export const Title = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;

  /* Desktop: 1 linha com ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Mobile: 2 linhas e legível */
  @media (max-width: 640px) {
    font-size: 18px;
    white-space: normal;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const Address = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;

  /* Desktop: 1 linha */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Mobile: 2 linhas */
  @media (max-width: 640px) {
    font-size: 14px;
    white-space: normal;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const MetaRow = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  align-items: center;

  /* Mobile: chips em 2 colunas alinhadas */
  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    align-items: stretch;
  }
`;

export const Meta = styled.span`
  font-size: 13px;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #eaecf0;
  border-radius: 999px;
  padding: 6px 10px;

  @media (max-width: 640px) {
    padding: 8px 10px;
  }
`;

const variants = {
    info: css`
    color: #1b4dd6;
    background: #e8f1ff;
    border: 1px solid #cfe0ff;
  `,
    warning: css`
    color: #a86700;
    background: #fff6e5;
    border: 1px solid #ffe1a8;
  `,
    success: css`
    color: #1b7a3a;
    background: #e7f8ee;
    border: 1px solid #c9efd8;
  `,
    neutral: css`
    color: #334155;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
  `,
} as const;

export const Status = styled.span<{ variant?: StatusVariant }>`
  font-size: 12px;
  font-weight: 800;
  border-radius: 999px;
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  line-height: 1;

  ${({ variant = "neutral" }) => variants[variant]}

  /* Mobile: status ocupa as duas colunas */
  @media (max-width: 640px) {
    grid-column: 1 / -1;
    padding: 8px 12px;
    font-size: 13px;
  }
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;

  @media (max-width: 640px) {
    grid-area: actions;
    justify-content: stretch;
  }
`;

export const Button = styled.button`
  border: 1px solid #d6d9e2;
  background: #ffffff;
  color: #111827;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;

  transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;

  &:hover {
    background: #f7f7fb;
    border-color: #cfd3de;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  @media (max-width: 640px) {
    width: 100%;
    padding: 12px 14px;
    font-size: 16px;
  }
`;

export const SkeletonText = styled.div`
  height: 14px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9edf3 37%, #f2f4f7 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: 0 0;
    }
  }
`;

export const EmptyState = styled.div`
  background: #ffffff;
  border: 1px dashed #d6d9e2;
  border-radius: 12px;
  padding: 18px;
  color: #6b7280;

  p {
    margin: 0;
  }
`;
